import React from 'react';
import { Helmet } from 'react-helmet-async';

const Privacy = () => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Privacy Policy - Trademark Inspire | How We Protect Your Personal Information</title>
        <meta name="description" content="Read Trademark Inspire's privacy policy. Learn how we collect, use, protect and manage your personal information. Updated January 2026. We respect your privacy and comply with all data protection regulations." />
        <meta name="keywords" content="privacy policy, data protection, personal information, GDPR compliance, data security, privacy rights" />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#093336] via-[#0a4548] to-[#093336] text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
            <p className="text-xl text-gray-100">Last Updated: January 2026</p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-[#093336] mb-4">1. Introduction</h2>
                <p className="text-gray-700">
                  Welcome to Trademark Inspire. We respect your privacy and are committed to protecting your personal data. This privacy policy will inform you about how we look after your personal data when you visit our website and tell you about your privacy rights and how the law protects you.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#093336] mb-4">2. Information We Collect</h2>
                <p className="text-gray-700 mb-3">
                  We may collect, use, store and transfer different kinds of personal data about you which we have grouped together as follows:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Identity Data:</strong> includes first name, last name, username or similar identifier.</li>
                  <li><strong>Contact Data:</strong> includes email address, telephone numbers, and mailing address.</li>
                  <li><strong>Technical Data:</strong> includes internet protocol (IP) address, browser type and version, time zone setting and location, and other technology on the devices you use to access this website.</li>
                  <li><strong>Usage Data:</strong> includes information about how you use our website and services.</li>
                  <li><strong>Marketing and Communications Data:</strong> includes your preferences in receiving marketing from us and your communication preferences.</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#093336] mb-4">3. How We Use Your Information</h2>
                <p className="text-gray-700 mb-3">
                  We will only use your personal data when the law allows us to. Most commonly, we will use your personal data in the following circumstances:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>To provide and maintain our services</li>
                  <li>To process your trademark applications and related services</li>
                  <li>To notify you about changes to our services</li>
                  <li>To provide customer support</li>
                  <li>To gather analysis or valuable information to improve our services</li>
                  <li>To monitor the usage of our services</li>
                  <li>To detect, prevent and address technical issues</li>
                  <li>To provide you with marketing communications (with your consent)</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#093336] mb-4">4. Data Security</h2>
                <p className="text-gray-700">
                  We have put in place appropriate security measures to prevent your personal data from being accidentally lost, used or accessed in an unauthorized way, altered or disclosed. We limit access to your personal data to those employees, agents, contractors and other third parties who have a business need to know. They will only process your personal data on our instructions and they are subject to a duty of confidentiality.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#093336] mb-4">5. Data Retention</h2>
                <p className="text-gray-700">
                  We will only retain your personal data for as long as reasonably necessary to fulfil the purposes we collected it for, including for the purposes of satisfying any legal, regulatory, tax, accounting or reporting requirements. We may retain your personal data for a longer period in the event of a complaint or if we reasonably believe there is a prospect of litigation in respect to our relationship with you.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#093336] mb-4">6. Your Legal Rights</h2>
                <p className="text-gray-700 mb-3">
                  Under certain circumstances, you have rights under data protection laws in relation to your personal data, including the right to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Request access to your personal data</li>
                  <li>Request correction of your personal data</li>
                  <li>Request erasure of your personal data</li>
                  <li>Object to processing of your personal data</li>
                  <li>Request restriction of processing your personal data</li>
                  <li>Request transfer of your personal data</li>
                  <li>Right to withdraw consent</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#093336] mb-4">7. Cookies</h2>
                <p className="text-gray-700">
                  Our website uses cookies to distinguish you from other users of our website. This helps us to provide you with a good experience when you browse our website and also allows us to improve our site. A cookie is a small file of letters and numbers that we store on your browser or the hard drive of your computer if you agree. Cookies contain information that is transferred to your computer's hard drive.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#093336] mb-4">8. Third-Party Links</h2>
                <p className="text-gray-700">
                  This website may include links to third-party websites, plug-ins and applications. Clicking on those links or enabling those connections may allow third parties to collect or share data about you. We do not control these third-party websites and are not responsible for their privacy statements. When you leave our website, we encourage you to read the privacy policy of every website you visit.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#093336] mb-4">9. Changes to This Privacy Policy</h2>
                <p className="text-gray-700">
                  We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top of this Privacy Policy. You are advised to review this Privacy Policy periodically for any changes.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-[#093336] mb-4">10. Contact Us</h2>
                <p className="text-gray-700">
                  If you have any questions about this Privacy Policy, please contact us:
                </p>
                <ul className="list-none space-y-2 text-gray-700 mt-3">
                  <li><strong>Email:</strong> Info@trademarkinspire.com</li>
                  <li><strong>Phone:</strong> +1 (888) 2029 517</li>
                  <li><strong>Address:</strong> 1652 W Texas St, Fairfield, CA 94533, United States</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
